import { FormEvent, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPatch, apiDelete } from "../lib/api";
import { useLibrary, usePlanHistory, queryKeys } from "../lib/queries";
import { LibraryCues } from "../components/LibraryCues";
import { LoadingState, SkeletonList, ErrorState } from "../components/Loading";
import {
  PlanElement,
  PlanGroup,
  PlanBody,
  WeightUnit,
  parsePlanBody,
  parseElements,
  describePrescription,
  describeCircuit,
  measureOf,
} from "../lib/planElements";
import { useAccountStatus } from "../lib/queries";
import { PlanDetail } from "../components/PlanDetail";
import { Modal } from "../components/Modal";
import { TrashIcon } from "../components/Icons";

interface PlanRow {
  id: string;
  client_id: string;
  title: string;
  status: "draft" | "published" | "archived";
  elements: string;
  published_at: string | null;
  created_at: string;
}

interface LibraryEntry {
  id: string;
  name: string;
  description: string | null;
  /** The coach's own category, so the picker can be narrowed by it (F52). */
  tag?: string;
  /** Present only when the entry really has a walkthrough -- what the picker's badge is driven by. */
  video_url?: string | null;
}

/** Form-local row: everything is a string while being typed, then narrowed on save. */
interface ExerciseRow {
  name: string;
  measure: "reps" | "time";
  sets: string;
  reps: string;
  durationSeconds: string;
  /** The number half of the load, in `loadUnit`. Empty when no load, or when `loadUnit` is "bodyweight". */
  loadValue: string;
  /** Chosen by the coach when they type the load -- what makes conversion for the athlete possible. */
  loadUnit: WeightUnit | "";
  /**
   * The plan's original free-text `load`, carried through untouched when it could not be confidently
   * parsed into a number and a unit (see `parseLegacyLoad`) -- so a plan from before this existed is
   * never silently wiped just because "resistance band" is not a number. Cleared the moment the coach
   * fills in `loadValue`/`loadUnit`, at which point the structured fields win.
   */
  legacyLoad: string;
  notes: string;
  restBetweenSetsSeconds: string;
  restAfterSeconds: string;
  libraryEntryId: string;
  /** The circuit this row is in, if any. Empty for a standalone exercise. */
  groupId: string;
}

const EMPTY_ROW: ExerciseRow = {
  name: "",
  // Reps is the common case and what every existing plan is, so a new row starts there and only
  // becomes time-measured if the coach says so.
  measure: "reps",
  sets: "",
  reps: "",
  durationSeconds: "",
  loadValue: "",
  loadUnit: "",
  legacyLoad: "",
  notes: "",
  restBetweenSetsSeconds: "",
  restAfterSeconds: "",
  libraryEntryId: "",
  groupId: "",
};

/**
 * A row seeded from a library entry.
 *
 * The whole point of the library is that the coach already wrote this exercise up once. Picking it
 * brings the name across and links the entry; the prescription (sets, reps or duration, load, rests)
 * stays blank because that is per-plan, not a property of the exercise.
 *
 * The library's cues are deliberately *not* copied into `notes`. They are read live from the entry, so
 * fixing a cue in the library reaches every plan pointing at it -- copying left plans repeating text
 * the coach had already corrected, with no way to find them short of opening every plan. `notes` is
 * what this athlete needs on top of the shared cues, and starts empty.
 */
function rowFromLibraryEntry(entry: LibraryEntry): ExerciseRow {
  return { ...EMPTY_ROW, name: entry.name, libraryEntryId: entry.id };
}

function toPlanElement(row: ExerciseRow): PlanElement {
  const element: PlanElement = { name: row.name.trim() };
  // Only set what was actually filled in -- an absent value is meaningful ("no prescribed load"),
  // and writing 0/"" instead would render as a real prescription of zero.
  //
  // `measure` is written only for time, so a reps exercise serialises exactly as it did before the
  // field existed and no existing plan changes shape on its next save.
  if (row.measure === "time") element.measure = "time";
  if (row.sets.trim()) element.sets = Number(row.sets);
  if (row.measure === "time") {
    if (row.durationSeconds.trim()) element.durationSeconds = Number(row.durationSeconds);
  } else if (row.reps.trim()) {
    element.reps = Number(row.reps);
  }
  // Bodyweight has no number to carry. A number is only written once the coach has picked a real
  // unit for it -- writing loadValue with no loadUnit would be a weight with nothing saying what it's
  // measured in, exactly the ambiguity this replaces.
  if (row.loadUnit === "bodyweight") {
    element.loadUnit = "bodyweight";
  } else if (row.loadUnit && row.loadValue.trim()) {
    element.loadValue = Number(row.loadValue);
    element.loadUnit = row.loadUnit;
  } else if (row.legacyLoad.trim()) {
    // Not yet migrated to a structured load -- e.g. "resistance band", which is not a number and a
    // unit. Carried through verbatim rather than silently dropped.
    element.load = row.legacyLoad.trim();
  }
  if (row.notes.trim()) element.notes = row.notes.trim();
  // Not `.trim()` truthiness: "0" is a real answer ("straight into the next one") and must survive.
  if (row.restBetweenSetsSeconds.trim() !== "") element.restBetweenSetsSeconds = Number(row.restBetweenSetsSeconds);
  if (row.restAfterSeconds.trim() !== "") element.restAfterSeconds = Number(row.restAfterSeconds);
  if (row.libraryEntryId) element.libraryEntryId = row.libraryEntryId;
  if (row.groupId) element.groupId = row.groupId;
  return element;
}

/**
 * Recovers a number and a unit from a legacy free-text load, so opening an old plan in the builder
 * offers a structured (and therefore convertible) load rather than an empty box the coach has to
 * retype from scratch.
 *
 * A bare number ("10", with no unit suffix) is exactly the shape that caused the reported bug -- typed
 * under the old free-text field with no way to say what it meant -- so it is read in `fallbackUnit`,
 * the coach's own current preference. That is a guess, but a visible and correctable one: it lands in
 * the unit chip already selected, where the coach sees it and can fix it before saving, rather than a
 * silent misinterpretation on someone else's screen. Anything that doesn't match this shape at all
 * (e.g. "resistance band") is left for `legacyLoad` to carry through untouched instead.
 */
function parseLegacyLoad(load: string | undefined, fallbackUnit: "lb" | "kg"): { value: string; unit: WeightUnit } | null {
  const trimmed = load?.trim();
  if (!trimmed) return null;
  if (/^body\s*weight$/i.test(trimmed)) return { value: "", unit: "bodyweight" };
  const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*(lb|kg)?$/i);
  if (!match) return null;
  const unit = (match[2]?.toLowerCase() as "lb" | "kg" | undefined) ?? fallbackUnit;
  return { value: match[1], unit };
}

function toExerciseRow(element: PlanElement, coachWeightUnit: "lb" | "kg"): ExerciseRow {
  const parsedLegacy = element.loadUnit ? null : parseLegacyLoad(element.load, coachWeightUnit);
  return {
    name: element.name,
    measure: measureOf(element),
    sets: element.sets?.toString() ?? "",
    reps: element.reps?.toString() ?? "",
    durationSeconds: element.durationSeconds?.toString() ?? "",
    loadValue:
      element.loadUnit === "bodyweight" ? "" : (element.loadValue?.toString() ?? parsedLegacy?.value ?? ""),
    loadUnit: element.loadUnit ?? parsedLegacy?.unit ?? "",
    // Only kept when parsing failed to produce a structured value -- once parsedLegacy succeeds, the
    // structured fields above already carry the same information and legacyLoad has nothing left to do.
    legacyLoad: element.loadUnit || parsedLegacy ? "" : (element.load ?? ""),
    notes: element.notes ?? "",
    restBetweenSetsSeconds: element.restBetweenSetsSeconds?.toString() ?? "",
    restAfterSeconds: element.restAfterSeconds?.toString() ?? "",
    libraryEntryId: element.libraryEntryId ?? "",
    groupId: element.groupId ?? "",
  };
}

/** Coach-facing plan builder: create/edit a draft, publish one, see and inspect full history (PRD §6.2). */
export function PlanBuilder() {
  const { clientId } = useParams<{ clientId: string }>();
  /**
   * The coach's own unit preference, used only to seed the load field when parsing a legacy plan --
   * see `parseLegacyLoad`. Never applied to a fresh row, which starts with no unit at all.
   */
  const coachWeightUnit = useAccountStatus().data?.weight_unit ?? "lb";
  const queryClient = useQueryClient();

  const historyQuery = usePlanHistory<PlanRow[]>(clientId);
  // A failed library fetch shouldn't block plan building -- linking a video is optional.
  const libraryQuery = useLibrary<LibraryEntry[]>("", "all");

  const [title, setTitle] = useState("");
  /**
   * Starts empty, not with one blank row.
   *
   * With a starter row, "Add exercise" had to decide whether to fill it or append -- and filling it
   * meant that on a fresh form the button appeared to do nothing at all. An empty list plus a prompt is
   * honest: there are no exercises yet, and the one button always adds one.
   */
  const [exerciseRows, setExerciseRows] = useState<ExerciseRow[]>([]);
  /**
   * The circuits over those rows (finding: circuit training).
   *
   * Held beside the rows rather than nested inside them, mirroring how a plan is stored: every logged
   * session records an index into a flat list of exercises, so nesting them would either invalidate a
   * year of history or turn that index into a path. A circuit is metadata over a contiguous run.
   */
  const [groups, setGroups] = useState<PlanGroup[]>([]);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [confirmingPublish, setConfirmingPublish] = useState<PlanRow | null>(null);
  /** What just happened, said out loud -- see the save mutation's onSuccess (F51). */
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState<PlanRow | null>(null);
  /**
   * The form sits above the history, so an action clicked further down the page changes something the
   * coach can't see. Focusing the title both brings it into view and puts the cursor on the one field
   * they are certain to change -- a copy of "Week 1" is almost always about to become "Week 2".
   */
  const titleRef = useRef<HTMLInputElement>(null);
  /** Which plan a copy came from, for the form's hint. Not sent anywhere -- a copy has no parent. */
  const [copiedFromTitle, setCopiedFromTitle] = useState<string | null>(null);
  /** Which tag the exercise picker is narrowed to, if any (F52). */
  const [pickerTag, setPickerTag] = useState<string>("all");
  /**
   * What the picker is currently for: adding a new exercise, or replacing the one already in a row.
   *
   * One control, two jobs. The coach asked to be able to change their mind about which exercise a row
   * is -- previously the only route was unlink, retype, relink, which is three steps to fix a misclick.
   */
  const [picker, setPicker] = useState<
    { mode: "add"; groupId?: string } | { mode: "replace"; index: number } | null
  >(null);
  const [librarySearch, setLibrarySearch] = useState("");

  function invalidateHistory() {
    return queryClient.invalidateQueries({ queryKey: queryKeys.planHistory(clientId ?? "") });
  }

  function resetForm() {
    setSavedNotice(null);
    setTitle("");
    setExerciseRows([]);
    setGroups([]);
    setEditingPlanId(null);
    setCopiedFromTitle(null);
  }

  /** Loads a plan's contents into the form. Shared by edit-in-place and copy-to-new-draft. */
  function loadIntoForm(plan: PlanRow) {
    const body = parsePlanBody(plan.elements);
    setTitle(plan.title);
    setExerciseRows(body.elements.map((element) => toExerciseRow(element, coachWeightUnit)));
    setGroups(body.groups);
    setExpandedPlanId(null);
  }

  const saveDraft = useMutation({
    mutationFn: (body: { title: string; elements: PlanBody }) =>
      editingPlanId ? apiPatch(`/plans/${editingPlanId}`, body) : apiPost(`/clients/${clientId}/plans`, body),
    onSuccess: async (_result, body) => {
      resetForm();
      // Saving cleared the form and added a card to History further down the page, with nothing said --
      // so from where the coach is looking, the button emptied his work and did nothing else (F51).
      setSavedNotice(`Saved "${body.title}" as a draft. It's in the history below, not published yet.`);
      await invalidateHistory();
    },
  });

  const unpublishPlan = useMutation({
    mutationFn: (planId: string) => apiPost(`/plans/${planId}/unpublish`),
    onSuccess: async (_result, planId) => {
      // Straight into editing it, since correcting the plan is the only reason to have done this.
      const plan = history?.find((p) => p.id === planId);
      if (plan) handleEditDraft({ ...plan, status: "draft" });
      await invalidateHistory();
    },
  });

  const publishPlan = useMutation({
    mutationFn: (planId: string) => apiPost(`/plans/${planId}/publish`),
    onSuccess: async () => {
      setConfirmingPublish(null);
      await invalidateHistory();
    },
  });

  const discardPlan = useMutation({
    mutationFn: (planId: string) => apiDelete(`/plans/${planId}`),
    onSuccess: async (_result, planId) => {
      setConfirmingDiscard(null);
      if (editingPlanId === planId) resetForm();
      await invalidateHistory();
    },
  });




  function updateGroup(id: string, patch: Partial<PlanGroup>) {
    setGroups((current) => current.map((group) => (group.id === id ? { ...group, ...patch } : group)));
  }

  function handleSaveDraft(event: FormEvent) {
    event.preventDefault();
    if (!clientId || !title.trim()) return;
    const elements = exerciseRows.filter((row) => row.name.trim().length > 0).map(toPlanElement);
    // Only circuits that still have exercises in them: a group whose rows were all deleted would
    // otherwise be stored forever as a round count over nothing.
    const usedGroups = groups.filter((group) => elements.some((element) => element.groupId === group.id));
    saveDraft.mutate({ title: title.trim(), elements: { elements, groups: usedGroups } });
  }

  function handleEditDraft(plan: PlanRow) {
    setEditingPlanId(plan.id);
    setCopiedFromTitle(null);
    loadIntoForm(plan);
  }

  /**
   * Start a new draft from an existing plan's contents.
   *
   * A published plan deliberately cannot be edited -- the server refuses a PATCH on anything but a
   * draft, because the family is training against it right now. That left the only path to next week's
   * plan being an empty form and retyping every exercise. This is the missing bridge: the source plan is
   * untouched, and publishing the copy archives it through the normal path.
   *
   * `setEditingPlanId(null)` is the line that matters. Without it, copying while a draft was open would
   * PATCH that draft instead of creating a plan -- and copying a *published* plan would then rewrite the
   * very thing not-editing-a-published-plan exists to protect.
   */
  function handleCopyToNewDraft(plan: PlanRow) {
    setEditingPlanId(null);
    setCopiedFromTitle(plan.title);
    loadIntoForm(plan);
    titleRef.current?.focus();
  }

  function updateExerciseRow(index: number, patch: Partial<ExerciseRow>) {
    setExerciseRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  /**
   * Applies a picker choice.
   *
   * `null` is the "blank exercise" option, which lives in the same list as the library entries rather
   * than behind a second button -- the coach wanted one way in, and a blank row is just another thing
   * you might want to add.
   *
   * Replacing keeps the prescription. Swapping "Squats" for "Front squats" should not silently wipe the
   * 3 x 8 you already typed; only the identity of the exercise changes.
   */
  function handlePick(entry: LibraryEntry | null) {
    const chosen = entry ? rowFromLibraryEntry(entry) : { ...EMPTY_ROW };
    const target = picker;

    setExerciseRows((rows) => {
      if (target?.mode === "replace") {
        return rows.map((row, i) =>
          i === target.index ? { ...row, name: chosen.name, libraryEntryId: chosen.libraryEntryId } : row
        );
      }

      /**
       * Into the circuit it was opened from, at the end of that circuit's run.
       *
       * Appended *inside* the group rather than at the bottom of the plan, because the group's exercises
       * have to stay contiguous -- that contiguity is what makes "the exercises in this circuit"
       * answerable by looking at the list, and what keeps every stored element index meaningful.
       */
      if (target?.mode === "add" && target.groupId) {
        const inGroup = rows.map((row, i) => (row.groupId === target.groupId ? i : -1)).filter((i) => i >= 0);
        const at = inGroup.length > 0 ? inGroup[inGroup.length - 1] + 1 : rows.length;
        const next = [...rows];
        next.splice(at, 0, { ...chosen, groupId: target.groupId });
        return next;
      }

      return [...rows, chosen];
    });

    setPicker(null);
    setLibrarySearch("");
  }

  /**
   * Adds an empty circuit, chosen from the same picker as everything else.
   *
   * A circuit is a container you then fill, rather than something an exercise is flagged into. That is
   * how a coach thinks about it -- "a conditioning circuit, and here is what is in it" -- and it means
   * the round count is set once, on the thing it belongs to.
   */
  function addCircuit() {
    const id = `g${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;
    setGroups((current) => [...current, { id, name: "", rounds: 3 }]);
    setPicker(null);
    setLibrarySearch("");
  }

  /**
   * Takes an exercise out of its circuit, and moves it clear of it.
   *
   * Moved to just after the circuit rather than left where it is, because a row that stayed put would
   * split the circuit's run in two -- and a circuit interleaved with other work is not a thing this
   * model can express, deliberately.
   */
  function removeFromCircuit(index: number) {
    setExerciseRows((rows) => {
      const row = rows[index];
      if (!row?.groupId) return rows;
      const inGroup = rows.map((r, i) => (r.groupId === row.groupId ? i : -1)).filter((i) => i >= 0);
      const last = inGroup[inGroup.length - 1];
      const without = rows.filter((_, i) => i !== index);
      const at = last > index ? last : last + 1;
      const next = [...without];
      next.splice(Math.min(at, next.length), 0, { ...row, groupId: "" });
      return next;
    });
  }

  /** Deletes a circuit and keeps its exercises, which become ordinary ones in the same place. */
  function removeCircuit(groupId: string) {
    setExerciseRows((rows) => rows.map((row) => (row.groupId === groupId ? { ...row, groupId: "" } : row)));
    setGroups((current) => current.filter((group) => group.id !== groupId));
  }

  /**
   * Moves one exercise up or down.
   *
   * Buttons rather than drag-and-drop: this is used on a phone as often as a laptop, dragging inside a
   * scrolling page is awkward on touch, and a drag handle is invisible to a keyboard.
   */
  function moveExercise(index: number, delta: number) {
    setExerciseRows((rows) => {
      const target = index + delta;
      if (target < 0 || target >= rows.length) return rows;
      const next = [...rows];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  /**
   * Whether the form holds work that has not been saved (finding #21).
   *
   * A draft only exists once "Save" is pressed, so a refresh or a click on "Back to clients" threw away
   * everything typed since -- with no warning, and no way to tell it had happened.
   */
  const hasUnsavedWork = title.trim().length > 0 || exerciseRows.some((row) => row.name.trim().length > 0);

  useEffect(() => {
    if (!hasUnsavedWork) return;
    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      // Browsers show their own wording now; returning a value is what triggers the prompt at all.
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedWork]);

  const history = historyQuery.data;
  const libraryEntries = libraryQuery.data ?? [];
  const actionError = saveDraft.error ?? publishPlan.error ?? discardPlan.error;
  /** The tags present in the library, for the picker's own filter chips. */
  const pickerTags = Array.from(new Set(libraryEntries.map((entry) => entry.tag).filter(Boolean) as string[])).sort(
    (a, b) => a.localeCompare(b)
  );
  const matchingLibraryEntries = libraryEntries.filter(
    (entry) =>
      entry.name.toLowerCase().includes(librarySearch.trim().toLowerCase()) &&
      (pickerTag === "all" || entry.tag === pickerTag)
  );
  const currentlyPublished = history?.find((p) => p.status === "published") ?? null;
  const planGroups = [
    { label: "Live now", plans: (history ?? []).filter((p) => p.status === "published") },
    { label: "Drafts", plans: (history ?? []).filter((p) => p.status === "draft") },
    { label: "History", plans: (history ?? []).filter((p) => p.status !== "published" && p.status !== "draft") },
  ].filter((group) => group.plans.length > 0);
  const viewedPlan = history?.find((p) => p.id === expandedPlanId) ?? null;

  /** The list's "New plan": an empty form, asking first if that would throw away something typed. */
  function startNewPlan() {
    if (hasUnsavedWork && !window.confirm("Start a new plan? What you've typed here will be lost.")) return;
    resetForm();
    setExpandedPlanId(null);
    titleRef.current?.focus();
  }

  return (
    // Inside the client's workspace now (the Plans tab), so the athlete's name, the way back and the
    // session log all live in the workspace around it rather than being repeated here.
    <div className="plan-builder">
      {actionError && <ErrorState error={actionError} />}
      {historyQuery.isError && <ErrorState error={historyQuery.error} onRetry={() => historyQuery.refetch()} />}

      <div className="plans-layout">
        {/* Every plan this athlete has, grouped by what it is to them right now -- the one they are on,
            the ones not given to them yet, and the ones that have run -- each with its own actions. */}
        <aside className="plans-list" aria-label="This athlete's plans">
          <button type="button" className="plans-new" onClick={startNewPlan}>
            New plan
          </button>

          {historyQuery.isPending && (
            <>
              <LoadingState label="Loading plan history…" />
              <SkeletonList rows={2} />
            </>
          )}

          {historyQuery.isSuccess && history && history.length === 0 && (
            <p className="empty-state">No plans yet. Write the first one and save it as a draft.</p>
          )}


          {planGroups.map((group) => (
            <section key={group.label} className="plans-group" aria-label={group.label}>
              <h3 className="plans-group-label">{group.label}</h3>
              <ul className="client-list">
                {group.plans.map((plan) => {
                  const elements = parseElements(plan.elements);
                  const expanded = expandedPlanId === plan.id;
                  return (
                    <li key={plan.id} className="client-card plan-card">
                      <div className="plan-card-main">
                        <span className="client-name">{plan.title}</span>
                      </div>
                      <p className="plan-summary">
                        {elements.length} exercise{elements.length === 1 ? "" : "s"}
                      </p>

                      <div className="filter-chips">
                        <button type="button" className="chip" onClick={() => setExpandedPlanId(expanded ? null : plan.id)}>
                          {expanded ? "Hide details" : "View details"}
                        </button>
                        {/* A published or archived plan can't be edited in place, so this is how last week's
                            programme becomes next week's without retyping it. A draft gets Edit instead --
                            copying one would only produce a second identical draft. */}
                        {plan.status !== "draft" && (
                          <button type="button" className="chip" onClick={() => handleCopyToNewDraft(plan)}>
                            Copy to new draft
                          </button>
                        )}
                        {/* Correcting a live plan, rather than only cloning it.
                            A published plan offered View details and Copy to new draft and nothing else, so a
                            typo or a wrong rep count meant copy, fix, publish -- which leaves the wrong plan
                            live in the meantime and fills the history with near-duplicates (UX review F50).
                            Unpublishing is the safe half: it becomes a draft the coach can edit, and the family
                            has no current plan until it goes back up, which is a state the app already
                            handles and says plainly. */}
                        {plan.status === "published" && (
                          <button
                            type="button"
                            className="chip"
                            disabled={unpublishPlan.isPending}
                            onClick={() => unpublishPlan.mutate(plan.id)}
                          >
                            {unpublishPlan.isPending ? "Unpublishing…" : "Unpublish to edit"}
                          </button>
                        )}
                        {plan.status === "draft" && (
                          <>
                            <button type="button" className="chip" onClick={() => handleEditDraft(plan)}>
                              Edit
                            </button>
                            <button type="button" className="chip" onClick={() => setConfirmingPublish(plan)}>
                              Publish
                            </button>
                            <button type="button" className="chip danger" onClick={() => setConfirmingDiscard(plan)}>
                              Discard
                            </button>
                          </>
                        )}
                      </div>

                      {confirmingPublish?.id === plan.id && (
                        <div className="confirm-box" role="group" aria-label="Confirm publish">
                          <p>
                            Publish <strong>{plan.title}</strong> with {elements.length} exercise
                            {elements.length === 1 ? "" : "s"}?{" "}
                            {currentlyPublished
                              ? `This replaces "${currentlyPublished.title}", which will be archived.`
                              : "The family will see it immediately."}
                          </p>
                          {/* The circuit, named, above the exercises it repeats. The summary listed a
                              3-round circuit's exercises as loose rows, so the coach confirming the publish
                              could not see the structure he had just built -- and two identically-named rows
                              side by side looked like a mistake rather than a loop (UX review F48). */}
                          <ul className="exercise-list">
                            {elements.map((el, i) => {
                              const prescription = describePrescription(el, Boolean(el.groupId));
                              const group = el.groupId
                                ? parsePlanBody(plan.elements).groups.find((g) => g.id === el.groupId)
                                : undefined;
                              const opensGroup = Boolean(group) && elements[i - 1]?.groupId !== el.groupId;
                              return (
                                <li key={i}>
                                  {opensGroup && group && (
                                    <span className="circuit-label">
                                      {`${group.name || "Circuit"} — ${describeCircuit(group)}`}
                                    </span>
                                  )}
                                  {el.name}
                                  {prescription ? ` — ${prescription}` : ""}
                                </li>
                              );
                            })}
                          </ul>
                          <button type="button" onClick={() => publishPlan.mutate(plan.id)} disabled={publishPlan.isPending}>
                            {publishPlan.isPending ? "Publishing…" : "Yes, publish it"}
                          </button>
                          <button type="button" className="chip" onClick={() => setConfirmingPublish(null)}>
                            Never mind
                          </button>
                        </div>
                      )}

                      {confirmingDiscard?.id === plan.id && (
                        <div className="confirm-box" role="group" aria-label="Confirm discard">
                          <p>
                            Discard the draft <strong>{plan.title}</strong>? This can't be undone.
                          </p>
                          <button type="button" onClick={() => discardPlan.mutate(plan.id)} disabled={discardPlan.isPending}>
                            {discardPlan.isPending ? "Discarding…" : "Yes, discard it"}
                          </button>
                          <button type="button" className="chip" onClick={() => setConfirmingDiscard(null)}>
                            Never mind
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </aside>

        <div className="plans-main">
          {viewedPlan ? (
            // A plan opened from the list, in full, where the form usually is.
            <div className="plan-form plan-viewer">
              <div className="plan-card-main">
                <h2>{viewedPlan.title}</h2>
                <span className={`badge badge-${viewedPlan.status}`}>{viewedPlan.status}</span>
              </div>
              <PlanDetail elements={parseElements(viewedPlan.elements)} groups={parsePlanBody(viewedPlan.elements).groups} />
              <button type="button" className="chip" onClick={() => setExpandedPlanId(null)}>
                {editingPlanId ? "Back to the draft" : "Back to new plan"}
              </button>
            </div>
          ) : (
            <form className="plan-form" onSubmit={handleSaveDraft}>
              <h2>{editingPlanId ? "Edit draft" : "New plan"}</h2>
              <p className="form-hint">
                {editingPlanId
                  ? "Saving updates this draft in place. It stays invisible to the family until you publish it."
                  : copiedFromTitle
                    ? `Starting from "${copiedFromTitle}". Saving creates a new draft — the original is untouched until you publish this one.`
                    : "Saving creates a draft. Drafts are invisible to the family until you publish one."}
              </p>

              <label htmlFor="plan-title">Plan title</label>
              <input id="plan-title" ref={titleRef} value={title} onChange={(e) => setTitle(e.target.value)} required />

              <p className="form-label">Exercises</p>
              {exerciseRows.length === 0 && (
                <p className="form-hint">No exercises yet. Add one from your library, or a blank one to type in.</p>
              )}
              {exerciseRows.map((row, index) => {
                const group = row.groupId ? groups.find((g) => g.id === row.groupId) : undefined;
                const startsGroup = Boolean(group) && exerciseRows[index - 1]?.groupId !== row.groupId;
                const endsGroup = Boolean(group) && exerciseRows[index + 1]?.groupId !== row.groupId;

                return (
                <div key={index}>
                {/*
                  The circuit's own header, rendered above the exercise that opens it.

                  A header rather than a nested container, because the rows underneath are still one flat list
                  -- which is what keeps a logged session's exercise index meaningful, and what lets an
                  exercise join or leave a circuit without anything being moved.
                */}
                {startsGroup && group && (
                  <div className="circuit-head">
                    <span className="element-tag">CIRCUIT</span>
                    <input
                      aria-label={`Circuit name for exercise ${index + 1}`}
                      placeholder="Circuit name (optional)"
                      value={group.name ?? ""}
                      onChange={(e) => updateGroup(group.id, { name: e.target.value })}
                    />
                    <input
                      aria-label={`${group.name || "Circuit"} rounds`}
                      type="number"
                      min="1"
                      max="20"
                      placeholder="Rounds"
                      value={group.rounds}
                      onChange={(e) => updateGroup(group.id, { rounds: Math.max(1, Number(e.target.value) || 1) })}
                    />
                    <input
                      aria-label={`${group.name || "Circuit"} rest between rounds in seconds`}
                      type="number"
                      min="0"
                      placeholder="Rest between rounds (s)"
                      value={group.restBetweenRoundsSeconds ?? ""}
                      onChange={(e) =>
                        updateGroup(group.id, {
                          restBetweenRoundsSeconds: e.target.value === "" ? undefined : Number(e.target.value),
                        })
                      }
                    />
                    {/* Keeps the exercises, which become ordinary ones where they already are. Deleting an
                        exercise is its own trash icon; removing the circuit should not take work with it. */}
                    <button
                      type="button"
                      className="chip quiet"
                      onClick={() => removeCircuit(group.id)}
                      title="The exercises stay in the plan"
                    >
                      Remove circuit
                    </button>
                  </div>
                )}
                <div className={group ? "exercise-editor in-circuit" : "exercise-editor"}>
                  <div className="exercise-head">
                    <span className="element-tag">E{index + 1}</span>
                    <input
                      aria-label={`Exercise ${index + 1} name`}
                      placeholder="Exercise name"
                      value={row.name}
                      onChange={(e) => updateExerciseRow(index, { name: e.target.value })}
                    />
                    {/* Reorder by button, not drag: this gets used on a phone, dragging inside a scrolling
                        page is awkward on touch, and a drag handle is invisible to a keyboard. */}
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={`Move ${row.name || `exercise ${index + 1}`} up`}
                      disabled={index === 0}
                      onClick={() => moveExercise(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={`Move ${row.name || `exercise ${index + 1}`} down`}
                      disabled={index === exerciseRows.length - 1}
                      onClick={() => moveExercise(index, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={`Remove ${row.name || `exercise ${index + 1}`}`}
                      onClick={() => setExerciseRows((rows) => rows.filter((_, i) => i !== index))}
                    >
                      <TrashIcon />
                    </button>
                  </div>

                  {/* Labels that stay put.
                      Every box here was labelled only by its placeholder, so the moment a coach typed
                      anything the label vanished and a row of six filled numbers had nothing saying which was
                      which -- exactly when you are checking what you just wrote (UX review F45). Persistent
                      labels above the inputs; the placeholders go, since the label now does that job.

                      Inside a circuit the set-level fields are gone rather than merely ignored: a circuit's
                      rounds ARE its sets, so offering an exercise its own "Sets" and "Rest between sets" was
                      offering a prescription the walkthrough would never use (F47).

                      One wrapping row rather than a line per group of fields: on a laptop the whole
                      prescription sits on one or two lines, and on a phone it wraps to the same pairs as
                      before. Three exercises used to run past a full screen. */}
                  <div className="exercise-fields">
                    {/* Reps or time. "Hold a plank for 30 seconds" is a real prescription reps cannot express,
                        and the field that follows changes with the answer rather than showing both. */}
                    <div className="field measure-field">
                      <span className="field-label">Measured in</span>
                      <div className="filter-chips" role="group" aria-label={`Exercise ${index + 1} measured in`}>
                        {(["reps", "time"] as const).map((measure) => (
                          <button
                            key={measure}
                            type="button"
                            className={`chip ${row.measure === measure ? "active" : ""}`}
                            aria-pressed={row.measure === measure}
                            onClick={() => updateExerciseRow(index, { measure })}
                          >
                            {measure === "reps" ? "Reps" : "Time"}
                          </button>
                        ))}
                      </div>
                    </div>
                    {!row.groupId && (
                      <label className="field">
                        <span className="field-label">Sets</span>
                        <input
                          aria-label={`Exercise ${index + 1} sets`}
                          type="number"
                          min="1"
                          value={row.sets}
                          onChange={(e) => updateExerciseRow(index, { sets: e.target.value })}
                        />
                      </label>
                    )}
                    {row.measure === "time" ? (
                      <label className="field">
                        <span className="field-label">Seconds</span>
                        <input
                          aria-label={`Exercise ${index + 1} seconds`}
                          type="number"
                          min="1"
                          value={row.durationSeconds}
                          onChange={(e) => updateExerciseRow(index, { durationSeconds: e.target.value })}
                        />
                      </label>
                    ) : (
                      <label className="field">
                        <span className="field-label">Reps</span>
                        <input
                          aria-label={`Exercise ${index + 1} reps`}
                          type="number"
                          min="1"
                          value={row.reps}
                          onChange={(e) => updateExerciseRow(index, { reps: e.target.value })}
                        />
                      </label>
                    )}
                    <div className="field load-field">
                      <span className="field-label">Load</span>
                      {/*
                        A unit, chosen here, is what makes conversion possible on the athlete's side. A bare
                        number used to be typed into free text with no unit at all, so an athlete logging in a
                        different unit than the coach was reading a number relabelled into their own unit
                        rather than converted -- "Coach said: 10 kg" when the coach meant 10 lb, which is wrong
                        in the dangerous direction for a child loading a bar.

                        Clicking the already-active chip clears it, matching the athlete's own weight-unit
                        control this deliberately mirrors, so there is one way to say "no load" everywhere.
                      */}
                      <div className="load-inputs">
                        <div className="filter-chips" role="group" aria-label={`Exercise ${index + 1} load unit`}>
                          {(["bodyweight", "lb", "kg"] as const).map((unit) => (
                            <button
                              key={unit}
                              type="button"
                              className={`chip ${row.loadUnit === unit ? "active" : ""}`}
                              aria-pressed={row.loadUnit === unit}
                              onClick={() =>
                                updateExerciseRow(index, {
                                  loadUnit: row.loadUnit === unit ? "" : unit,
                                  legacyLoad: "",
                                })
                              }
                            >
                              {unit === "bodyweight" ? "Bodyweight" : unit}
                            </button>
                          ))}
                        </div>
                        {row.loadUnit && row.loadUnit !== "bodyweight" && (
                          <input
                            aria-label={`Exercise ${index + 1} load`}
                            type="number"
                            min="0"
                            step="any"
                            value={row.loadValue}
                            onChange={(e) => updateExerciseRow(index, { loadValue: e.target.value })}
                          />
                        )}
                      </div>
                      {/* A legacy free-text load that could not be parsed into a number and a unit --
                          "resistance band", say. Shown so the coach knows it is still there rather than
                          silently gone, with a way to replace it by picking a unit above. */}
                      {row.legacyLoad && (
                        <p className="form-hint">
                          Previously recorded as &ldquo;{row.legacyLoad}&rdquo;. Pick a unit above to replace
                          it with a number, or leave as is to keep it unchanged.
                        </p>
                      )}
                    </div>

                    {/* Two different pauses, and conflating them is the reason they are labelled this
                        explicitly: one is between sets of this exercise, the other is before the next one.

                        Both placeholders now say what blank means. They looked identical and behaved
                        differently: an empty "between sets" gave the athlete no rest, while an empty "before
                        next" gave them a full-screen 60-second countdown from a constant that appeared nowhere
                        in this UI. So a coach prescribed a pace he had not chosen and could not see, and the
                        athlete sat through a minute nobody meant her to have (UX review F61). Blank is "none"
                        in both now, and the field says so rather than leaving it to be discovered. */}
                    {/* Between-set rest is a set-level field, so it goes inside a circuit too (F47) -- the
                        circuit's own "rest between rounds" is the one that applies there. */}
                    {!row.groupId && (
                      <label className="field rest-field">
                        <span className="field-label">Rest between sets (s)</span>
                        <input
                          aria-label={`Exercise ${index + 1} rest between sets in seconds`}
                          type="number"
                          min="0"
                          placeholder="none if blank"
                          value={row.restBetweenSetsSeconds}
                          onChange={(e) => updateExerciseRow(index, { restBetweenSetsSeconds: e.target.value })}
                        />
                      </label>
                    )}
                    <label className="field rest-field">
                      <span className="field-label">Rest before next (s)</span>
                      <input
                        aria-label={`Exercise ${index + 1} rest before next exercise in seconds`}
                        type="number"
                        min="0"
                        placeholder="none if blank"
                        value={row.restAfterSeconds}
                        onChange={(e) => updateExerciseRow(index, { restAfterSeconds: e.target.value })}
                      />
                    </label>
                  </div>

                  {/* The library's own cues, shown read-only because they are not this plan's to edit: they
                      belong to the exercise, and every plan referencing it sees the same text. Editing them
                      is what the library page is for, and doing it there updates all of these at once. */}
                  {row.libraryEntryId && (
                    <div className="library-cues">
                      <p className="form-label">Library cues</p>
                      <LibraryCues entryId={row.libraryEntryId} className="library-cue-text" />
                      <p className="form-hint">Edit these in the library and every plan using this exercise follows.</p>
                    </div>
                  )}

                  {/* Cues for this athlete, on top of the library's. This is what the athlete reads while
                      doing the exercise, next to the shared cues rather than instead of them. */}
                  <textarea
                    aria-label={`Exercise ${index + 1} coaching cues`}
                    placeholder={row.libraryEntryId ? "Extra cues for this athlete" : "Coaching cues"}
                    value={row.notes}
                    onChange={(e) => updateExerciseRow(index, { notes: e.target.value })}
                  />

                  <div className="exercise-actions">
                    {row.libraryEntryId ? (
                      <span className="badge">From your library</span>
                    ) : (
                      <span className="form-hint">No video — added by hand</span>
                    )}
                    {/* Change, not unlink-then-retype. Swapping "Squats" for "Front squats" was three steps
                        and lost what you had typed; this keeps the prescription and only changes which
                        exercise the row is. */}
                    <button type="button" className="chip" onClick={() => setPicker({ mode: "replace", index })}>
                      {row.libraryEntryId ? "Change exercise" : "Pick from library"}
                    </button>
                    {row.libraryEntryId && (
                      // Quieter than Change: these detach rather than add, and they are rarely what the coach came for.
                      <button type="button" className="chip quiet" onClick={() => updateExerciseRow(index, { libraryEntryId: "" })}>
                        Unlink
                      </button>
                    )}

                    {/* Out of the circuit, for a mis-added exercise. Deleting it is the trash icon above. */}
                    {group && (
                      <button type="button" className="chip quiet" onClick={() => removeFromCircuit(index)}>
                        Take out of circuit
                      </button>
                    )}
                  </div>
                </div>

                {/* The circuit's own way in, after the last exercise in it -- which is where a coach is
                    looking when they think "and then...". */}
                {group && endsGroup && (
                  <button
                    type="button"
                    className="chip circuit-add"
                    onClick={() => setPicker({ mode: "add", groupId: group.id })}
                  >
                    Add exercise to {group.name || "this circuit"}
                  </button>
                )}
                </div>
                );
              })}

              {/* A circuit added but not yet filled. It has no exercises to hang a header off, so it gets its
                  own card -- otherwise choosing "Circuit" would appear to do nothing at all. */}
              {groups
                .filter((group) => !exerciseRows.some((row) => row.groupId === group.id))
                .map((group) => (
                  <div key={group.id} className="circuit-head empty">
                    <span className="element-tag">CIRCUIT</span>
                    <input
                      aria-label={`Circuit name for ${group.id}`}
                      placeholder="Circuit name (optional)"
                      value={group.name ?? ""}
                      onChange={(e) => updateGroup(group.id, { name: e.target.value })}
                    />
                    <input
                      aria-label={`${group.name || "Circuit"} rounds`}
                      type="number"
                      min="1"
                      max="20"
                      placeholder="Rounds"
                      value={group.rounds}
                      onChange={(e) => updateGroup(group.id, { rounds: Math.max(1, Number(e.target.value) || 1) })}
                    />
                    <input
                      aria-label={`${group.name || "Circuit"} rest between rounds in seconds`}
                      type="number"
                      min="0"
                      placeholder="Rest between rounds (s)"
                      value={group.restBetweenRoundsSeconds ?? ""}
                      onChange={(e) =>
                        updateGroup(group.id, {
                          restBetweenRoundsSeconds: e.target.value === "" ? undefined : Number(e.target.value),
                        })
                      }
                    />
                    <button
                      type="button"
                      className="chip"
                      onClick={() => setPicker({ mode: "add", groupId: group.id })}
                    >
                      Add exercise to {group.name || "this circuit"}
                    </button>
                    <button type="button" className="chip quiet" onClick={() => removeCircuit(group.id)}>
                      Remove circuit
                    </button>
                  </div>
                ))}

              {/* One way in. The blank option lives inside the picker's list rather than behind a second
                  button next to it -- adding a blank exercise is just another thing you might pick. */}
              <button type="button" className="chip" onClick={() => setPicker({ mode: "add" })}>
                Add exercise
              </button>

              <button type="submit" disabled={saveDraft.isPending}>
                {saveDraft.isPending ? "Saving…" : editingPlanId ? "Save changes to draft" : "Save as new draft"}
              </button>
              {editingPlanId && (
                <button type="button" className="chip" onClick={resetForm}>
                  Cancel editing
                </button>
              )}
              {/* Says which plan was saved and, just as importantly, that it is NOT published -- the two
                  buttons sit together and the difference is the whole point (F51). */}
              {savedNotice && (
                <p className="badge badge-ok" role="status">
                  {savedNotice}
                </p>
              )}
            </form>
          )}
        </div>
      </div>

      {picker && (
        <Modal
          title={
            picker.mode === "replace"
              ? "Change this exercise"
              : picker.groupId
                ? "Add an exercise to this circuit"
                : "Add to this plan"
          }
          onClose={() => {
            setPicker(null);
            setLibrarySearch("");
          }}
        >
          {/* Filtered here rather than by refetching: the whole library is already loaded for this
              page, and a coach scanning for "box" shouldn't wait on a round trip to see it. */}
          <label htmlFor="library-picker-search">Search your library</label>
          <input
            id="library-picker-search"
            value={librarySearch}
            onChange={(e) => setLibrarySearch(e.target.value)}
            placeholder="Exercise name"
          />

          {/* Filter by tag, and say which entries have a walkthrough.
              The picker offered a name search over the whole library and nothing else -- no way to
              narrow by the coach's own categories, and no indication of which exercises actually have
              a video, which is the thing worth knowing while choosing what a nine-year-old will be
              asked to do unsupervised (UX review F52). */}
          {pickerTags.length > 0 && (
            <div className="filter-chips" role="group" aria-label="Filter the picker by tag">
              <button
                type="button"
                className={pickerTag === "all" ? "chip active" : "chip"}
                onClick={() => setPickerTag("all")}
              >
                All
              </button>
              {pickerTags.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={pickerTag === t ? "chip active" : "chip"}
                  onClick={() => setPickerTag(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          )}

          <ul className="client-list">
            {/* A blank exercise is an option in the list, not a separate button beside it. A one-off
                ("easy spin, 5 minutes") isn't worth a library entry, but it is still just something you
                are adding to the plan. Hidden while searching, where it would be a non-matching result. */}
            {!librarySearch.trim() && (
              <li>
                <button type="button" className="library-picker-entry" onClick={() => handlePick(null)}>
                  <span className="client-name">Blank exercise</span>
                  <span className="plan-summary">Type it in yourself. No linked video.</span>
                </button>
              </li>
            )}
            {/* A circuit is added the same way as anything else, and then filled from its own button.
                Not offered from inside a circuit -- a circuit within a circuit is not a thing this
                model expresses, and pretending otherwise would only produce a confusing failure.
                Shown regardless of the search box (27 Sep debug notes item 10): unlike "Blank exercise",
                this isn't a non-matching library result, it's a structural option, and hiding it the
                moment a coach starts typing -- which they do immediately, looking for an exercise to add
                first -- was very likely the whole "what happened to circuits?" report (never reproduced
                any other way: circuits are intact everywhere else they're wired). */}
            {picker.mode === "add" && !picker.groupId && (
              <li>
                <button type="button" className="library-picker-entry" onClick={addCircuit}>
                  <span className="client-name">Circuit</span>
                  <span className="plan-summary">
                    A group of exercises done in order and repeated. Add the exercises to it afterwards.
                  </span>
                </button>
              </li>
            )}
            {matchingLibraryEntries.map((entry) => (
              <li key={entry.id}>
                <button type="button" className="library-picker-entry" onClick={() => handlePick(entry)}>
                  <span className="client-name">
                    {entry.name}
                    {entry.video_url && <span className="badge badge-ok">Video</span>}
                  </span>
                  {entry.description && <span className="plan-summary">{entry.description}</span>}
                </button>
              </li>
            ))}
          </ul>

          {matchingLibraryEntries.length === 0 &&
            (librarySearch.trim() ? (
              <p className="empty-state">No exercise matches &ldquo;{librarySearch}&rdquo;.</p>
            ) : (
              <p className="form-hint">
                Nothing in your library yet. Add exercises on the Library page to reuse them across plans.
              </p>
            ))}
        </Modal>
      )}


    </div>
  );
}
